/* ============================================================
   OBF CART GIFTS — quantity-driven free-gift controller (Solstice)

   Ported & simplified from the live theme's obf-cart-guard.js.
   The live guard depended on the bundle widget stamping gift
   variant IDs onto shoe line items. Solstice has no bundle widget,
   so this version is driven purely by the total shoe quantity in
   the cart and a fixed pair of gift variant IDs supplied by the
   cart-drawer snippet via window.OBF_GIFTS_CONFIG.

   Tier rules (total NON-GIFT shoe quantity in cart):
     qty < 2  -> no gifts
     qty == 2 -> CloudStep Insoles only          (tier 1)
     qty >= 3 -> CloudStep Insoles + ShieldWash Bag (tier 2)

   Gift line items are stamped with property _obf_gift ('insoles' |
   'bag') so the drawer can render them locked, and so this guard
   can find / cap / remove them. The matching automatic BxGy
   discounts (CSI / CSIWASH) zero their price at checkout.

   Defends against:
   - Gift line quantity bumped above 1            -> force back to 1
   - Duplicate gift lines of the same type        -> remove extras
   - Gifts left in cart after shoes drop a tier    -> remove
   - $0 gift added via devtools without earning it -> removed as tier 0
   ============================================================ */

(function () {
  'use strict';

  var CFG = window.OBF_GIFTS_CONFIG || {};
  var INSOLE_VID = String(CFG.insoleVariantId || '');
  var BAG_VID = String(CFG.bagVariantId || '');
  var GIFT_PRODUCT_IDS = (CFG.giftProductIds || []).map(String);

  // If we have no gift variant IDs there is nothing to enforce.
  if (!INSOLE_VID && !BAG_VID) return;

  var GUARD_FLAG = '__obf_cart_gifts_running';
  var DEBOUNCE_MS = 150;
  var debounceTimer = null;

  function tierFor(qty) {
    if (qty < 2) return 0;
    if (qty === 2) return 1;
    return 2;
  }

  function isGiftLine(it) {
    var p = it.properties || {};
    if (p._obf_gift) return true;
    // Defensive: also treat known gift products as gifts even if the
    // property is missing (e.g. added directly via /cart/add.js).
    return GIFT_PRODUCT_IDS.indexOf(String(it.product_id)) !== -1;
  }

  function validateCart(fromMutation) {
    if (window[GUARD_FLAG]) return;
    if (window.__obf_modal_updating) return; // variant modal mid-swap
    window[GUARD_FLAG] = true;

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart || !cart.items) { window[GUARD_FLAG] = false; return; }

        var shoeQty = 0;
        var insoleLines = [];
        var bagLines = [];

        cart.items.forEach(function (it) {
          var p = it.properties || {};
          if (p._obf_gift === 'insoles') { insoleLines.push(it); return; }
          if (p._obf_gift === 'bag') { bagLines.push(it); return; }
          if (isGiftLine(it)) return; // unknown-type gift, skip from shoe count
          shoeQty += it.quantity;
        });

        var tier = tierFor(shoeQty);
        var fixes = [];     // { id, quantity } -> /cart/change.js
        var addsNeeded = []; // { variantId, properties } -> /cart/add.js

        // --- Insoles: present in tier 1 and 2 ---
        reconcileGift(insoleLines, tier >= 1, INSOLE_VID, 'insoles', fixes, addsNeeded);
        // --- Bag: present only in tier 2 ---
        reconcileGift(bagLines, tier >= 2, BAG_VID, 'bag', fixes, addsNeeded);

        if (!fixes.length && !addsNeeded.length) {
          window[GUARD_FLAG] = false;
          // No gift change needed, but a real cart mutation (qty change,
          // remove, upsell add) still needs the redesigned drawer to
          // re-render its totals / progress bar. Skip on initial page load.
          if (fromMutation) refreshCartDrawer();
          return;
        }

        var chain = Promise.resolve();
        fixes.forEach(function (f) {
          chain = chain.then(function () {
            return fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: f.id, quantity: f.quantity })
            }).then(function (r) { return r.json(); });
          });
        });
        addsNeeded.forEach(function (a) {
          chain = chain.then(function () {
            return fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: a.variantId, quantity: 1, properties: a.properties })
            }).then(function (r) { return r.json(); });
          });
        });

        chain
          .then(function () {
            window[GUARD_FLAG] = false;
            ['cart:refresh', 'cart:updated', 'cart-drawer:refresh'].forEach(function (n) {
              document.dispatchEvent(new CustomEvent(n, { bubbles: true }));
              window.dispatchEvent(new CustomEvent(n, { bubbles: true }));
            });
            refreshCartDrawer();
          })
          .catch(function (err) {
            console.warn('[OBF gifts] reconciliation failed', err);
            window[GUARD_FLAG] = false;
          });
      })
      .catch(function () { window[GUARD_FLAG] = false; });
  }

  /* Keep exactly one gift line of a type when wanted, none otherwise. */
  function reconcileGift(lines, wanted, variantId, giftKey, fixes, addsNeeded) {
    if (wanted) {
      if (lines.length === 0) {
        if (variantId) {
          addsNeeded.push({ variantId: variantId, properties: { _obf_gift: giftKey } });
        }
      } else {
        // Keep the first line capped at qty 1, remove any extras.
        if (lines[0].quantity > 1) fixes.push({ id: lines[0].key, quantity: 1 });
        for (var i = 1; i < lines.length; i++) {
          fixes.push({ id: lines[i].key, quantity: 0 });
        }
      }
    } else {
      lines.forEach(function (l) { fixes.push({ id: l.key, quantity: 0 }); });
    }
  }

  /* Re-render the drawer from the server section, then reload upsells. */
  function refreshCartDrawer() {
    var drawer = document.querySelector('#Cart-Drawer .side-panel-inner');
    if (!drawer) return;
    fetch('/?sections=cart-drawer')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (!data || !data['cart-drawer']) return;
        var parser = new DOMParser();
        var doc = parser.parseFromString(data['cart-drawer'], 'text/html');
        var newInner = doc.querySelector('#Cart-Drawer .side-panel-inner');
        if (newInner) drawer.innerHTML = newInner.innerHTML;
        if (window.initRecommendationTracks) window.initRecommendationTracks();
      })
      .catch(function () { /* silent */ });
  }

  function schedule(fromMutation) {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () { validateCart(fromMutation); }, DEBOUNCE_MS);
  }

  /* Patch fetch so any cart mutation triggers a validation pass. */
  var originalFetch = window.fetch;
  window.fetch = function (input) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var isCartMutation =
      url.indexOf('/cart/add') !== -1 ||
      url.indexOf('/cart/change') !== -1 ||
      url.indexOf('/cart/update') !== -1 ||
      url.indexOf('/cart/clear') !== -1;
    var p = originalFetch.apply(this, arguments);
    if (isCartMutation && !window[GUARD_FLAG]) {
      p.then(function () { schedule(true); }, function () {});
    }
    return p;
  };

  /* Patch XMLHttpRequest too (older theme code paths). */
  var originalOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__obfUrl = url || '';
    return originalOpen.apply(this, arguments);
  };
  var originalSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function () {
    var u = this.__obfUrl || '';
    var isCartMutation =
      u.indexOf('/cart/add') !== -1 ||
      u.indexOf('/cart/change') !== -1 ||
      u.indexOf('/cart/update') !== -1 ||
      u.indexOf('/cart/clear') !== -1;
    if (isCartMutation && !window[GUARD_FLAG]) {
      this.addEventListener('loadend', function () { schedule(true); });
    }
    return originalSend.apply(this, arguments);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { schedule(false); });
  } else {
    schedule(false);
  }

  /* Public hooks. OBFCartGuard kept as an alias so the ported
     variant-change modal (which calls window.OBFCartGuard.validate)
     works unchanged. */
  window.OBFGiftGuard = {
    validate: function () { schedule(true); },
    validateNow: function () { validateCart(true); }
  };
  window.OBFCartGuard = window.OBFCartGuard || window.OBFGiftGuard;
})();
